import { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from "jwt-decode";
import {
  getAllTabless,
  putTableOccupied,
  putTableFree,
  getAllCategories,
  createOrder,
  createBillForOrder,
  getBillsPaginated,
  getTenBills,
} from '../services/api';
import { useSelector } from 'react-redux';

const POSContext = createContext();

export function POSProvider({ children }) {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tables, setTables] = useState([]);
  const [activeTable, setActiveTable] = useState(null);
  const [orders, setOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orderHistory, setOrderHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [cartItems, setCartItems] = useState([]);
  const [currentBill, setCurrentBill] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const { id } = useSelector((state) => state.auth);

  // Récupération des tables
  useEffect(() => {
    const fetchTables = async () => {
      try {
        const { data } = await getAllTabless(0, 100);
        const fetchedTables = data.tables || data.content || [];
        setTables(Array.isArray(fetchedTables) ? fetchedTables : []);
      } catch (err) {
        console.error('Erreur lors du chargement des tables', err);
      }
    };
    fetchTables();
  }, []);

  // Récupération des catégories et produits
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('Token d\'authentification manquant');
          return;
        }
        const { data } = await getAllCategories(0, 100, token);
        console.log('Catégories récupérées:', data);

        // Extraire les noms des catégories
        const categoryNames = data.content.map(category => category.name);

        setCategories(categoryNames);

        // Transformer les menuItems en produits avec une propriété category
        const allProducts = data.content.flatMap(category =>
          category.menuItems.map(item => ({
            ...item,
            category: category.name
          }))
        );
        setProducts(allProducts);
        console.log('Produits transformés:', allProducts);

        // Définir la première catégorie comme active
        if (categoryNames.length > 0) {
          setActiveCategory(categoryNames[0]);
        }
      } catch (err) {
        console.error('Erreur lors du chargement des catégories:', err);
        setCategories([]);
        setProducts([]);
      }
    };
    fetchCategories();
  }, []);

  // Création de commande
  const createNewOrder = async () => {
    try {
      if (!cartItems || cartItems.length === 0) {
        console.warn("Panier vide, aucune commande à envoyer.");
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Token d'authentification manquant");
        return;
      }

      const decoded = jwtDecode(token);
      const createdAt = new Date().toISOString().split('.')[0];

      const orderData = {
        status: "PENDING",
        createdAt: createdAt,
        creatorId: id,
        tableId: activeTable?.id || 1,
        orderItem: cartItems.map(item => ({
          quantity: item.quantity,
          menuItem: {
            id: item.id
          }
        }))
      };

      const response = await createOrder(orderData, token);
      const order = response.data;

      const billRes = await createBillForOrder(order.id);
      const bill = billRes.data;

      const grandTotal = cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
      setCurrentOrder(order);
      setOrders(prev => [...prev, order]);
      setTotal(grandTotal);
      setCurrentBill(bill);
      console.log("Commande et facture créées avec succès");
    } catch (error) {
      console.error("Erreur lors de la création de la commande :", error);
    }
  };

  // Finalisation de la commande
  const completeOrder = async (orderId) => {
    try {
      const completedOrder = {
        ...currentOrder,
        status: 'completed',
        completedAt: new Date().toISOString(),
        total
      };

      setOrders(prev => prev.map(order =>
        order.id === orderId ? completedOrder : order
      ));
      setOrderHistory(prev => [...prev, completedOrder]);

      if (completedOrder.tableId) {
        setTables(prev => prev.map(table =>
          table.id === completedOrder.tableId
            ? { ...table, status: 'free', lastReceipt: completedOrder }
            : table
        ));
      }

      setCurrentOrder(null);
      setActiveTable(null);
      setCartItems([]);
      setCurrentBill(null);
    } catch (err) {
      console.error("Erreur lors de la finalisation de la commande :", err);
    }
  };

  // Réinitialiser pour une nouvelle commande
  const resetForNewOrder = () => {
    setCurrentBill(null);
    setCurrentOrder(null);
    setCartItems([]);
    setTotal(0);
  };

  // Historique par table
  const getTableHistory = (tableId) =>
    orderHistory.filter(order => order.tableId === tableId);

  // Récupérer la dernière facture d'une table
  const fetchLastBillForTable = async (tableId) => {
    try {
      const res = await getBillsPaginated(0, 10);
      const bills = res.data?.content || [];
      const tableBills = bills.filter(bill => bill.order?.table?.id === tableId);
      const lastBill = tableBills.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
      setSelectedBill(lastBill || null);
      return lastBill;
    } catch (err) {
      console.error('Erreur lors de la récupération de la dernière facture', err);
      setSelectedBill(null);
      return null;
    }
  };

  // Récupérer les 10 dernières factures d'une table
  const fetchLast10BillsForTable = async (tableId) => {
    try {
      const res = await getTenBills(tableId);
      const bills = res.data?.content || res.data || [];
      return bills;
    } catch (err) {
      console.error('Erreur lors de la récupération des dernières factures', err);
      return [];
    }
  };

  const removeFromCart = (productId) => {
  setCartItems(prev => prev.filter(item => item.id !== productId));
};
  // Ajouter un produit au panier
  const addToCart = (product) => {
    setCurrentBill(null);
    setCartItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prev, { ...product, quantity: 1 }];
      }
    });
  };

  return (
    <POSContext.Provider
      value={{
        categories,
        products,
        activeCategory,
        setActiveCategory,
        removeFromCart, 
        searchQuery,
        setSearchQuery,
        getTableHistory,
        tables,
        setTables,
        activeTable,
        setActiveTable,
        currentOrder,
        setCurrentOrder,
        orders,
        orderHistory,
        total,
        addToCart,
        completeOrder,
        cartItems,
        setCartItems,
        createNewOrder,
        currentBill,
        setCurrentBill,
        resetForNewOrder,
        fetchLastBillForTable,
        fetchLast10BillsForTable,
        selectedBill,
        setSelectedBill
      }}
    >
      {children}
    </POSContext.Provider>
  );
}

export function usePOS() {
  return useContext(POSContext);
}